import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { useEnsureGroup } from "../hooks/useEnsureGroup";
import { usePageSetup } from "../hooks/usePageSetup";
import { selectMember, useGroupStore } from "../store/groupStore";
import { PageHeader } from "../components/PageHeader";
import { ErrorState, Skeleton } from "../components/ScreenStates";
import { RatingSelectionWorkspace } from "../components/rating/RatingSelectionWorkspace";
import { fullName } from "../utils/format";

/** Loads the condition and its ratings, then hands the keyed workspace the data it needs. */
export function SelectRatingPage() {
  const { groupId = "", memberId = "", conditionId: conditionParam = "" } = useParams();
  const conditionId = Number(conditionParam);
  const ensure = useEnsureGroup(groupId);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const member = useGroupStore(selectMember(memberId));
  const condition = useAsyncResource(() => getApi().getCondition(conditionId), `condition-${conditionId}`, "SelectRatingPage.condition");
  const ratings = useAsyncResource(() => getApi().getConditionRatings(conditionId), `ratings-${conditionId}`, "SelectRatingPage.ratings");
  const memberName = member ? fullName(member.firstName, member.lastName) : "Member";
  const breadcrumb = useMemo(() => (group && member ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }, { label: memberName, to: `/uw/groups/${group.id}/members/${member.id}` }, { label: condition.data?.name ?? "Select Rating", to: "" }] : null), [group, member, memberName, condition.data]);
  usePageSetup("Select Rating", breadcrumb);

  if (ensure.error) return <ErrorState message={ensure.error} onRetry={ensure.retry} />;
  if (condition.state.kind === "error") return <ErrorState message={condition.state.message} onRetry={condition.reload} />;
  if (ratings.state.kind === "error") return <ErrorState message={ratings.state.message} onRetry={ratings.reload} />;
  if (!group || !member || !condition.data || !ratings.data) {
    if (!ensure.isLoading && group && !member) return <ErrorState message="Member not found in this group." onRetry={ensure.retry} />;
    return (
      <div className="space-y-5">
        <PageHeader title="Loading ratings" />
        <div className="space-y-2 rounded-2xl bg-white p-5 shadow-md" role="status" aria-label="Loading ratings">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return <RatingSelectionWorkspace key={`${member.id}-${conditionId}`} group={group} member={member} condition={condition.data} ratings={ratings.data} />;
}
